import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Warning, Trash } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Presentation labels for each reset group key returned by the backend.
const GROUP_LABELS = {
  invoices: 'Factures',
  quotes: 'Devis',
  credit_notes: 'Avoirs',
  clients: 'Clients',
  cash_sessions: 'Sessions & mouvements de caisse',
  stock_movements: 'Mouvements de stock',
  interventions: 'Interventions',
  sav: 'SAV',
  losses: 'Pertes / rebuts',
  catalog_keys: 'Catalogue — Clés',
  catalog_it_products: 'Catalogue — Produits IT',
  catalog_services: 'Catalogue — Interventions à domicile',
  catalog_printing: 'Catalogue — Impressions',
  catalog_business_cards: 'Catalogue — Cartes de visite',
  catalog_binding: 'Catalogue — Reliure',
};

const DangerZone = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState(new Set());

  useEffect(() => {
    axios
      .get(`${API_URL}/api/admin/reset-data/groups`, { withCredentials: true })
      .then((r) => setAvailableGroups(r.data.groups || []))
      .catch(() => setAvailableGroups([]));
  }, []);

  const toggleGroup = (key) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => setSelectedGroups(new Set(availableGroups.map((g) => g.key)));
  const selectNone = () => setSelectedGroups(new Set());

  const handleReset = async () => {
    if (confirmPhrase !== 'RESET-LBC') {
      toast.error('Tapez exactement : RESET-LBC');
      return;
    }
    if (!password) {
      toast.error('Mot de passe requis');
      return;
    }
    if (selectedGroups.size === 0) {
      toast.error('Sélectionnez au moins une catégorie à supprimer');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/reset-data`,
        {
          password,
          confirm_phrase: confirmPhrase,
          groups: Array.from(selectedGroups),
        },
        { withCredentials: true },
      );
      setResult(r.data);
      toast.success(`Réinitialisation OK — ${r.data.total_deleted} documents supprimés`);
      setPassword('');
      setConfirmPhrase('');
      setSelectedGroups(new Set());
      setShowDialog(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  const allSelected = availableGroups.length > 0 && selectedGroups.size === availableGroups.length;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-slate-900 mb-2">Maintenance</h2>
      <div className="border-2 border-red-300 rounded-lg p-5 bg-red-50">
        <div className="flex items-start gap-3 mb-4">
          <Warning size={28} className="text-red-600 flex-shrink-0" weight="fill" />
          <div>
            <h3 className="text-lg font-semibold text-red-900">Zone de danger — Réinitialiser les données</h3>
            <p className="text-sm text-red-800 mt-1">
              Sélectionnez précisément les catégories que vous souhaitez purger.
              Les catégories non cochées ne seront pas touchées.
            </p>
            <p className="text-sm text-red-800 mt-2">
              <strong>Toujours préservés</strong> : utilisateurs, paramètres entreprise, fournisseurs, codes postaux.
            </p>
            <p className="text-xs text-red-700 mt-2 font-semibold">
              ⚠️ Action irréversible. Faites une sauvegarde de votre base si nécessaire.
            </p>
          </div>
        </div>

        {/* Selection grid */}
        <div className="bg-white border border-red-200 rounded-md p-4 mb-4" data-testid="reset-group-selector">
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm font-semibold text-slate-800">Choisissez ce que vous voulez supprimer :</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={selectAll}
                data-testid="reset-select-all"
                className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
              >
                Tout cocher
              </button>
              <button
                type="button"
                onClick={selectNone}
                data-testid="reset-select-none"
                className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
              >
                Tout décocher
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {availableGroups.map((g) => (
              <label
                key={g.key}
                className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer transition-colors ${
                  selectedGroups.has(g.key)
                    ? 'bg-red-50 border-red-300'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedGroups.has(g.key)}
                  onChange={() => toggleGroup(g.key)}
                  data-testid={`reset-group-${g.key}`}
                  className="h-4 w-4 accent-red-600"
                />
                <span className="text-sm text-slate-800">{GROUP_LABELS[g.key] || g.key}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3">
            {selectedGroups.size === 0
              ? 'Aucune catégorie sélectionnée.'
              : `${selectedGroups.size} catégorie${selectedGroups.size > 1 ? 's' : ''} sélectionnée${selectedGroups.size > 1 ? 's' : ''}${allSelected ? ' (tout)' : ''}.`}
          </p>
        </div>

        <Button
          onClick={() => setShowDialog(true)}
          disabled={selectedGroups.size === 0}
          data-testid="open-reset-dialog-btn"
          className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Trash size={18} className="mr-2" />
          Supprimer la sélection
        </Button>

        {result && (
          <div className="mt-5 p-4 bg-white border border-slate-200 rounded-md" data-testid="reset-result">
            <p className="font-semibold text-slate-900 mb-2">
              ✅ Réinitialisation terminée — {result.total_deleted} documents supprimés
            </p>
            <details className="text-sm text-slate-600">
              <summary className="cursor-pointer">Détails par collection</summary>
              <ul className="mt-2 space-y-1">
                {Object.entries(result.deleted || {}).map(([k, v]) => (
                  <li key={k}>
                    <code className="text-xs">{k}</code> : {v} supprimés
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}
      </div>

      <Dialog
        open={showDialog}
        onOpenChange={(o) => {
          setShowDialog(o);
          if (!o) {
            setPassword('');
            setConfirmPhrase('');
          }
        }}
      >
        <DialogContent data-testid="reset-confirm-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Warning size={22} weight="fill" /> Confirmation requise
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Vous allez supprimer <strong>{selectedGroups.size}</strong> catégorie
              {selectedGroups.size > 1 ? 's' : ''} :
            </p>
            <ul className="text-sm text-slate-600 list-disc list-inside">
              {Array.from(selectedGroups).map((k) => (
                <li key={k}>{GROUP_LABELS[k] || k}</li>
              ))}
            </ul>
            <p className="text-sm text-red-700 font-medium">
              Action <strong>irréversible</strong>.
            </p>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Tapez <code className="px-1 py-0.5 bg-slate-100 rounded text-red-700 font-bold">RESET-LBC</code> pour confirmer
              </label>
              <Input
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                data-testid="reset-confirm-phrase-input"
                placeholder="RESET-LBC"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Votre mot de passe administrateur</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="reset-cancel-btn">
              Annuler
            </Button>
            <Button
              onClick={handleReset}
              disabled={loading || confirmPhrase !== 'RESET-LBC' || !password || selectedGroups.size === 0}
              data-testid="reset-confirm-btn"
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              {loading ? 'Suppression en cours…' : 'Supprimer définitivement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DangerZone;
